/**
 * product-variant-media.js
 *
 * Two features on the product page gallery:
 *
 * 1. ALT-TAG IMAGE FILTERING (client-side)
 *    Images whose alt text follows the #OptionFirstWord_Value convention are
 *    filtered purely in the browser when the variant changes.
 *    Each tagged <li> carries data-alt-filter="OptionFirstWord:value".
 *    <media-gallery> carries data-option-names='["Color","Size",...]'.
 *    On variantChange: JS navigates to the first tagged image for the selected
 *    color, then falls back to the variant's featured_media if nothing matches.
 *
 * 2. VARIANT VIDEO METAFIELD (custom.video_url)
 *    <media-gallery> carries data-variant-videos='{"variantId":"url",...}'.
 *    Inspired by DORO's native-video reset approach: whenever the user navigates
 *    AWAY from the video slide, we reset the deferred-media back to its poster
 *    state (remove `loaded` attr + live <video> element). This guarantees that
 *    every visit to the video slide triggers a clean loadContent() call:
 *    poster → user clicks → video plays. No fighting with pauseAllMedia().
 */
(function () {

  // --------------------------------------------------------------------------
  // Helpers
  // --------------------------------------------------------------------------

  function buildOptionMap(variant, optionNames) {
    var map = {};
    var values = [variant.option1, variant.option2, variant.option3];
    optionNames.forEach(function (name, i) {
      var firstWord = name.split(' ')[0];
      map[firstWord] = (values[i] || '').toLowerCase();
    });
    return map;
  }

  // Preload all images inside an element so they're cached before the slide
  // becomes visible. Uses <link rel=preload imagesrcset> so the browser fetches
  // the SAME srcset candidate it will render (not just the fallback src).
  // A data-attr flag prevents duplicate <link> tags on repeated calls.
  function preloadImagesIn(el) {
    if (!el) return;
    el.querySelectorAll('img').forEach(function (img) {
      // Always mark eager so it loads the moment its parent becomes visible
      img.loading = 'eager';
      if (img.dataset.preloadQueued) return;
      img.dataset.preloadQueued = '1';
      // Inject <link rel=preload as=image> — fires fetch from outside display:none
      var link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      if (img.srcset) {
        link.setAttribute('imagesrcset', img.srcset);
        link.setAttribute('imagesizes', img.sizes || '100vw');
      }
      if (img.src) link.href = img.src;
      document.head.appendChild(link);
    });
  }

  function applyAltFilter(mediaGallery, optionMap) {
    // Find the first tagged item that matches the current variant and return it for navigation.
    function isMatch(altFilter) {
      var conditions = altFilter.split('|');
      for (var c = 0; c < conditions.length; c++) {
        var parts = conditions[c].split(':');
        var selectedValue = optionMap[parts[0]];
        if (selectedValue !== undefined && selectedValue !== (parts[1] || '')) return false;
      }
      return true;
    }

    var allSlides = mediaGallery.querySelectorAll('li[data-media-id]');
    var firstUnfiltered = null;
    for (var i = 0; i < allSlides.length; i++) {
      var filter = allSlides[i].dataset.altFilter;
      if (!filter) {
        if (!firstUnfiltered) firstUnfiltered = allSlides[i];
        continue;
      }
      if (isMatch(filter)) return allSlides[i];
    }
    return firstUnfiltered || allSlides[0] || null;
  }

  function syncAltFilteredMedia(mediaGallery, optionMap) {
    function isMatch(altFilter) {
      var conditions = altFilter.split('|');
      for (var c = 0; c < conditions.length; c++) {
        var parts = conditions[c].split(':');
        var selectedValue = optionMap[parts[0]];
        if (selectedValue !== undefined && selectedValue !== (parts[1] || '')) return false;
      }
      return true;
    }

    mediaGallery.querySelectorAll('li[data-alt-filter]').forEach(function (item) {
      var filter = item.dataset.altFilter;
      var shouldHide = !!filter && !isMatch(filter);
      item.hidden = shouldHide;
      if (shouldHide) {
        item.style.setProperty('display', 'none', 'important');
        item.setAttribute('aria-hidden', 'true');
      } else {
        item.style.removeProperty('display');
        item.removeAttribute('aria-hidden');
      }
    });
  }

  function getSelectedVariantData(sectionId) {
    var selectedVariantScript = document.querySelector('#variant-selects-' + sectionId + ' [data-selected-variant]');
    if (!selectedVariantScript) return null;

    try {
      return JSON.parse(selectedVariantScript.textContent);
    } catch (e) {
      return null;
    }
  }

  function updateGalleryForVariant(mediaGallery, variant) {
    if (!variant) return null;

    var optionNamesAttr = mediaGallery.getAttribute('data-option-names');
    if (!optionNamesAttr) return null;

    var optionNames;
    try { optionNames = JSON.parse(optionNamesAttr); } catch (e) { optionNames = []; }
    if (!optionNames.length) return null;

    var optionMap = buildOptionMap(variant, optionNames);
    syncAltFilteredMedia(mediaGallery, optionMap);
    return applyAltFilter(mediaGallery, optionMap);
  }

  function scrollToMediaSlide(mediaGallery, mediaId) {
    var target = mediaGallery.querySelector('[data-media-id="' + mediaId + '"]');
    if (!target || !target.parentElement) return;

    if (typeof target.parentElement.scrollTo === 'function') {
      target.parentElement.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
      return;
    }

    if (typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }

  function navigateTo(mediaGallery, mediaId) {
    if (!mediaId) return;
    if (typeof mediaGallery.setActiveMedia === 'function') {
      mediaGallery.setActiveMedia(mediaId, false);
      scrollToMediaSlide(mediaGallery, mediaId);
    } else {
      var viewer = mediaGallery.querySelector('[id^="GalleryViewer-"] ul');
      var target = mediaGallery.querySelector('[data-media-id="' + mediaId + '"]');
      if (viewer && target) {
        viewer.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
      }
    }
  }

  /**
   * Reset a deferred-media element back to its poster state.
   * Mirrors what DORO's custom.js does for native-video on variant change:
   * remove the live element, remove the `loaded` guard, restore the poster.
   * The <template> stays untouched so the next loadContent() call works cleanly.
   */
  function resetDeferredMedia(deferred) {
    if (!deferred) return;

    // Remove live video (or any already-cloned media child)
    var liveVideo = deferred.querySelector('video');
    if (liveVideo) liveVideo.remove();

    // Lift the one-shot guard so loadContent() fires properly next time
    deferred.removeAttribute('loaded');

    // Restore poster visibility (loadContent hides it via display:none in some themes)
    var poster = deferred.querySelector('[id^="Deferred-Poster-"]');
    if (poster) poster.style.display = '';
  }

  // --------------------------------------------------------------------------
  // Patch setActiveMedia on a gallery to reset the metafield video slide
  // whenever the user navigates AWAY from it.
  // --------------------------------------------------------------------------

  function patchGalleryForVideoReset(mediaGallery) {
    if (mediaGallery._videoResetPatched) return;
    mediaGallery._videoResetPatched = true;

    var original = mediaGallery.setActiveMedia.bind(mediaGallery);
    mediaGallery.setActiveMedia = function (mediaId, prepend) {
      // Reset any video slide we're navigating away from so next visit is fresh
      mediaGallery.querySelectorAll('[data-variant-video-slide]').forEach(function (slide) {
        if (slide.dataset.mediaId !== mediaId) {
          resetDeferredMedia(slide.querySelector('.deferred-media'));
        }
      });
      return original(mediaId, prepend);
    };
  }

  // --------------------------------------------------------------------------
  // Variant change handler
  // --------------------------------------------------------------------------

  function handleVariantChange(event) {
    var variant = event.data && event.data.variant;
    if (!variant) return;

    var sectionId = event.data.sectionId;
    var mediaGallery = sectionId
      ? document.querySelector('#MediaGallery-' + sectionId)
      : document.querySelector('media-gallery');
    if (!mediaGallery) return;

    // Re-apply the patch in case AJAX replaced the gallery instance
    if (typeof mediaGallery.setActiveMedia === 'function') {
      patchGalleryForVideoReset(mediaGallery);
    }

    // --- 1. Hide non-matching tagged media and navigate to the first matching item ---
    var firstVisible = updateGalleryForVariant(mediaGallery, variant);
    if (firstVisible) {
      navigateTo(mediaGallery, firstVisible.dataset.mediaId);
      return;
    }

    // Fallback to the variant featured media if no tagged image matched.
    if (variant.featured_media) {
      navigateTo(mediaGallery, sectionId + '-' + variant.featured_media.id);
    }

    // --- 2. Variant video metafield (per-color slides) ---
    // All video slides stay visible at all times — video resets are handled by
    // patchGalleryForVideoReset when the user navigates to a different slide.
  }

  // --------------------------------------------------------------------------
  // Init
  // --------------------------------------------------------------------------

  function init() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, handleVariantChange);
    }

    document.querySelectorAll('media-gallery').forEach(function (gallery) {
      if (typeof gallery.setActiveMedia === 'function') {
        patchGalleryForVideoReset(gallery);
      }

      var sectionId = gallery.id.replace('MediaGallery-', '');
      var selectedVariant = getSelectedVariantData(sectionId);
      var firstVisible = updateGalleryForVariant(gallery, selectedVariant);
      if (firstVisible) {
        navigateTo(gallery, firstVisible.dataset.mediaId);
      }
    });
  }

  // global.js deferred scripts run before DOMContentLoaded, so MediaGallery
  // is already defined; but setActiveMedia is only available after the custom
  // element constructor runs (on element upgrade). Use a short rAF to be safe.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      requestAnimationFrame(init);
    });
  } else {
    requestAnimationFrame(init);
  }

})();
